import { ENDPOINT } from '~/constants/endpoints';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type UpdateUnitMappingPayload,
  type UpdateUnitMappingResponse,
} from './types';
import {
  zodUpdateUnitMappingEndpointSchema,
  zodUpdateUnitMappingSearchParamsSchema,
} from './validations';

/**
 * Updates an existing unit mapping from an invalid UN/CEFACT unit to a valid UN/CEFACT unit.
 *
 * @param {Object} unitMappingData - The data for updating an existing unit mapping.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Units/operation/update_unit_mapping_units_unit_mapping__id__put
 */
export const updateUnitMapping = async ({
  unitMappingId,
  unitMappingData,
}: UpdateUnitMappingPayload) => {
  try {
    const response = await vestigasApi
      .put(ENDPOINT.UNITS.UPDATE_MAPPING(unitMappingId), {
        json: unitMappingData,
        validate: {
          body: zodUpdateUnitMappingSearchParamsSchema,
          pathSegments: zodUpdateUnitMappingEndpointSchema,
        },
      })
      .json<UpdateUnitMappingResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error updating unit mapping',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 409: {
          errorMessage = i18n.t(($) => $.unit.updateUnitMapping.error409, {
            ns: 'data',
          });
          break;
        }

        case 422: {
          errorMessage = i18n.t(($) => $.unit.updateUnitMapping.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.unit.updateUnitMapping.error.generic, {
            ns: 'data',
          });
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
